import { type FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatScore } from 'FormatScore';

/*
    Arc geometry: https://stackoverflow.com/questions/5736398/how-to-calculate-the-svg-path-for-an-arc-of-a-circle
    NOTE: the rounding must match getExamScore, or a 1-lesson exam stream shows an exam score that differs from
    the lesson score.
*/

const START_ANGLE = 135; // bottom left
const FINISH_ANGLE = 360 + 45; // bottom right

// Without this the stroke gets clipped at the edges of the viewBox.
const PADDING = 2;

type Props = {
    score?: number | null;
    radius?: number;
    strokeWidth?: number;
    scoreFontSize?: number;
    className?: string;
};

const polarToCartesian = (center: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return [center + radius * Math.cos(angleInRadians), center + radius * Math.sin(angleInRadians)].join(',');
};

export const AssessmentScoreArc: FC<Props> = ({
    score,
    radius = 30,
    strokeWidth = 8,
    scoreFontSize = 18,
    className,
}) => {
    const size = 2 * radius + strokeWidth + 2 * PADDING;
    const center = radius + strokeWidth / 2 + PADDING;
    const pointAt = (angle: number) => polarToCartesian(center, radius, angle);

    const scoreForCalculations = score || 0;
    const filledDegrees = scoreForCalculations * (FINISH_ANGLE - START_ANGLE);
    const largeArcFlag = filledDegrees < 180 ? 0 : 1;
    const filledFinishAngle = START_ANGLE + filledDegrees;

    // With no score at all, the text matches the empty track rather than the filled arc.
    const scoreTextClass = score === undefined || score === null ? 'fill-slate-grey-400' : 'fill-turquoise-400';

    return (
        <svg viewBox={`0 0 ${size} ${size}`} version="1.1" preserveAspectRatio="xMinYMin meet" className={className}>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <path
                    d={`M ${pointAt(START_ANGLE)} A ${radius} ${radius} 0 1 1 ${pointAt(FINISH_ANGLE)}`}
                    className="stroke-slate-grey-400"
                    opacity="0.8"
                    strokeWidth={strokeWidth}
                />
                <path
                    d={`M ${pointAt(START_ANGLE)} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${pointAt(filledFinishAngle)}`}
                    className="stroke-turquoise-400"
                    opacity="0.8"
                    strokeWidth={strokeWidth}
                />
                <text
                    fontSize={scoreFontSize}
                    textAnchor="middle"
                    x={center}
                    y={center + 3}
                    // ProximaNovaSoft is the font-lesson-player family, so it comes from the theme
                    // rather than from a raw fontFamily attribute.
                    className={twMerge('font-lesson-player font-bold', scoreTextClass)}
                >
                    {formatScore(scoreForCalculations)}%
                </text>
            </g>
        </svg>
    );
};
